import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { SignupStateService, SESSION_DOC_FIELDS } from './services/signup-state.service';
import { SignupSessionData } from './services/firestore-storage.service';

/**
 * The merged storage record handed to components. Values are intentionally
 * untyped: entries mix session-doc fields, JSON-parsed localStorage values,
 * and UI step flags, and consumers narrow per key.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StorageRecord = Record<string, any>;

/**
 * Facade over the app's two storage concerns:
 * - Signup form data lives in the Firestore-backed SignupStateService (the
 *   session doc is the single source of truth; SDK persistence handles
 *   offline/local caching).
 * - UI-only step flags (LOGIN, VENUE, PLAN, ...) stay in localStorage —
 *   they are per-device navigation state, not signup data.
 *
 * setItem/getAllStorage/storage$ transparently route by key, so components
 * keep using one API for both.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionStorageService {
  private localSubject = new BehaviorSubject<StorageRecord>({});
  storage$: Observable<StorageRecord>;

  constructor(private signupState: SignupStateService) {
    this.localSubject.next(this.readLocalStorage());

    window.addEventListener('storage', () => {
      this.localSubject.next(this.readLocalStorage());
    });

    this.storage$ = combineLatest([this.localSubject, this.signupState.state$]).pipe(
      map(([local, state]) => ({ ...local, ...state }))
    );
  }

  setItem(key: string, value: unknown): void {
    if ((SESSION_DOC_FIELDS as string[]).includes(key)) {
      this.signupState.patch({ [key]: value } as Partial<SignupSessionData>);
      return;
    }
    if (typeof value === 'string') {
      localStorage.setItem(key, value);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
    this.localSubject.next(this.readLocalStorage());
  }

  getAllStorage(): StorageRecord {
    return { ...this.readLocalStorage(), ...this.signupState.snapshot() };
  }

  clearAllStorage(): void {
    localStorage.clear();
    this.localSubject.next({});
    this.signupState.resetLocal();
  }

  // ---- Typed accessors for common fields ----

  getUserEmail(): string {
    return this.signupState.snapshot().userEmail || '';
  }

  setUserEmail(email: string): void {
    this.signupState.patch({ userEmail: email });
  }

  getUserLocale(): string {
    return this.signupState.snapshot().userLocale || 'en-US';
  }

  setUserLocale(locale: string): void {
    this.signupState.patch({ userLocale: locale });
  }

  getAgreeTerms(): boolean {
    return this.signupState.snapshot().agreeTerms === true;
  }

  setAgreeTerms(agreed: boolean): void {
    this.signupState.patch({ agreeTerms: agreed });
  }

  getBusinessName(): string {
    return this.signupState.snapshot().venueName || '';
  }

  setBusinessName(businessName: string): void {
    this.signupState.patch({ venueName: businessName });
  }

  // ---- localStorage helpers ----

  private readLocalStorage(): StorageRecord {
    const entries: StorageRecord = {};

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) {
        continue;
      }
      const rawValue = localStorage.getItem(key);
      if (!rawValue) {
        entries[key] = null;
        continue;
      }

      // Parse values that look like JSON (objects, arrays, booleans, numbers)
      const trimmedValue = rawValue.trim();
      if (
        trimmedValue.startsWith('{') ||
        trimmedValue.startsWith('[') ||
        trimmedValue === 'true' ||
        trimmedValue === 'false' ||
        trimmedValue === 'null' ||
        !isNaN(Number(trimmedValue))
      ) {
        try {
          entries[key] = JSON.parse(rawValue);
        } catch (error) {
          entries[key] = rawValue;
        }
      } else {
        entries[key] = rawValue;
      }
    }

    return entries;
  }
}
